<template>
  <div>
    <!-- Controls -->
    <div class="ibox">
      <div class="ibox-content">
        <div
          class="grid grid-cols-1 p-m p-l-sm-xs-only p-r-n-xs-only align-items-center"
        >
          <div class="m-t-m">
            <div class="flex flex-wrap justify-space-between align-items-start">
              <div>
                <h1 class="font-heavy m-t-none">{{ eventName }}</h1>
                <p v-if="!loading">
                  {{ totalTeamCount }} teams · {{ poolEntries.length }} total
                  wrestlers · {{ unassignedPoolCount }} unassigned wrestlers
                </p>
              </div>
              <div class="m-x-m">
                <report-form
                  name="Team Registration Roster Report"
                  type="TeamRegistrationRosterReport"
                  version="vrow"
                  :prefilled-paid-session-id="paidSessionId"
                  button-text="Export Roster"
                  first-col-classes=""
                  second-col-classes=""
                  btn-classes="btn-default"
                ></report-form>
              </div>
            </div>
            <hr />
          </div>
          <div v-if="loading" class="text-center p-y-md">
            <spinner></spinner>
          </div>
          <div v-else>
            <!-- Add wrestlers -->
            <div class="row m-none">
              <div class="col-md-4">
                <h1 class="form-section-title">
                  Add Wrestlers
                </h1>
                <p class="text-gray-600">
                  Parents apply through your application link. You'll review
                  and approve each application from this page before they
                  join a team.
                </p>
              </div>
              <div class="col-md-8 flex flex-col gap-md">
                <!-- Application link (recommended path) -->
                <div
                  v-if="registrationUrl"
                  class="bg-white relative border border-solid border-green-600 rounded-xxs"
                >
                  <div
                    class="flex align-items-center justify-center gap-sm bg-green-600 text-green-50 uppercase tracking-wide p-x-m p-y-xs rounded-t-xxs"
                  >
                    <span class="text-heavy">WIQ Recommendation</span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke-width="1.5"
                      stroke="currentColor"
                      class="w-6 h-6 text-green-50"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18"
                      />
                    </svg>
                  </div>
                  <div class="p-x-lg p-y-sm">
                    <p class="font-heavy m-b-xxs">
                      Send this link to parents to apply for this team
                      registration.
                    </p>
                    <a
                      :href="registrationUrl"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="text-blue-600 text-semi"
                      style="word-break: break-all"
                    >
                      {{ registrationUrl }}
                    </a>
                    <div class="flex gap-xs flex-wrap m-t-xs">
                      <button
                        class="btn btn-primary btn-sm"
                        @click="copyRegistrationUrl"
                      >
                        <i class="fa-solid fa-paste"></i> Copy link
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Admin override (secondary) -->
                <div
                  class="bg-white relative border border-solid border-red-600 rounded-xxs"
                >
                  <div
                    class="flex align-items-center justify-center gap-sm bg-red-600 text-red-50 uppercase tracking-wide p-x-m p-y-xs rounded-t-xxs"
                  >
                    <span class="text-heavy">Admin Override</span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke-width="1.5"
                      stroke="currentColor"
                      class="w-6 h-6 text-red-50"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                      />
                    </svg>
                  </div>
                  <div class="p-x-lg p-y-sm">
                    <p class="m-b-xs">
                      Skip the application/approval flow and add a wrestler
                      directly. Their parent won't have signed liability
                      forms or saved a card on file.
                    </p>
                    <button
                      class="btn btn-danger btn-outline btn-sm"
                      @click="showAddWrestler = true"
                      v-if="!showAddWrestler"
                    >
                      <i class="fa-solid fa-plus"></i>
                      Manually add wrestler
                    </button>

                  <!-- Add wrestler panel -->
                    <div
                      v-if="showAddWrestler"
                      class="p-sm border border-solid rounded-sm border-gray-200 bg-gray-50 w-full"
                    >
                      <div
                        class="flex justify-space-between align-items-center m-b-sm"
                      >
                        <strong>{{
                          selectedWrestlerForAdd
                            ? "Confirm Registration"
                            : "Add Wrestler"
                        }}</strong>
                        <button
                          class="btn btn-xs btn-default"
                          @click="closeAddWrestler"
                        >
                          <i class="fa-solid fa-xmark"></i>
                        </button>
                      </div>

                      <!-- Step 1: Search -->
                      <template v-if="!selectedWrestlerForAdd">
                        <input
                          type="text"
                          v-model="addWrestlerSearch"
                          placeholder="Search team wrestlers..."
                          class="form-control m-b-sm font-no-ios-zoom"
                          @input="onAddWrestlerSearch"
                        />
                        <div
                          v-if="addWrestlerLoading"
                          class="text-center p-y-sm"
                        >
                          <spinner></spinner>
                        </div>
                        <div
                          v-else-if="addWrestlerResults.length > 0"
                          class="list-group"
                        >
                          <a
                            v-for="wrestler in addWrestlerResults"
                            :key="wrestler.id"
                            class="list-group-item cursor-pointer"
                            :class="{ disabled: isAlreadyInPool(wrestler.id) }"
                            @click="selectWrestlerForAdd(wrestler)"
                          >
                            <div class="flex align-items-center gap-sm">
                              <img
                                v-if="wrestler.avatar_thumbnail"
                                :src="wrestler.avatar_thumbnail"
                                class="rounded-full w-8 h-8"
                                style="object-fit: cover"
                              />
                              <div
                                v-else
                                class="rounded-full w-8 h-8 bg-gray-200 flex align-items-center justify-center"
                              >
                                <i class="fa-solid fa-user text-gray-400"></i>
                              </div>
                              <div>
                                <strong>{{ wrestler.full_name }}</strong>
                                <span
                                  v-if="isAlreadyInPool(wrestler.id)"
                                  class="text-gray-400 text-12"
                                >
                                  (already in pool)</span
                                >
                              </div>
                              <div class="align-self-flex-end">
                                <span v-if="wrestler.dob"
                                  >Born {{ wrestler.dob }}.
                                  {{ wrestler.age }} years old.</span
                                >
                                <span>{{ wrestler.weight_class }} pounds.</span>
                              </div>
                            </div>
                          </a>
                        </div>
                        <div
                          v-else-if="
                            addWrestlerSearch.length >= 2 && !addWrestlerLoading
                          "
                          class="text-gray-500 text-center p-y-sm"
                        >
                          No wrestlers found.
                        </div>
                      </template>

                      <!-- Step 2: Confirmation -->
                      <template v-else>
                        <div class="m-b-sm">
                          <div class="flex align-items-center gap-sm m-b-sm">
                            <img
                              v-if="selectedWrestlerForAdd.avatar_thumbnail"
                              :src="selectedWrestlerForAdd.avatar_thumbnail"
                              class="rounded-full w-8 h-8"
                              style="object-fit: cover"
                            />
                            <div
                              v-else
                              class="rounded-full w-8 h-8 bg-gray-200 flex align-items-center justify-center"
                            >
                              <i class="fa-solid fa-user text-gray-400"></i>
                            </div>
                            <strong>{{
                              selectedWrestlerForAdd.full_name
                            }}</strong>
                            <button
                              class="btn btn-xs btn-default"
                              @click="
                                selectedWrestlerForAdd = null;
                                agreedToTerms = false;
                              "
                            >
                              Change
                            </button>
                          </div>
                        </div>

                        <div
                          v-if="
                            paidSession.payment_options &&
                            paidSession.payment_options.length > 1
                          "
                          class="m-b-sm"
                        >
                          <label class="font-semi text-12"
                            >Payment Option</label
                          >
                          <select
                            v-model="paymentOptionId"
                            class="form-control input-sm"
                          >
                            <option
                              v-for="option in paidSession.payment_options"
                              :key="option.id"
                              :value="option.id"
                            >
                              {{ option.name }}
                            </option>
                          </select>
                        </div>

                        <div
                          class="p-sm rounded-sm"
                          style="background: #fef2f2; border: 1px solid #fecaca"
                        >
                          <div class="font-semi m-b-xs">
                            <i
                              class="fa-solid fa-triangle-exclamation"
                              style="color: #dc2626"
                            ></i>
                            Please read carefully
                          </div>
                          <ul class="text-12 m-t-xxs m-l-none p-l-m m-b-xs">
                            <li>
                              It is your responsibility to ensure the wrestler
                              has all liability forms signed by a
                              parent/guardian.
                            </li>
                            <li>
                              If your team uses USA Wrestling, AAU, or other
                              card based insurance, this process will skip those
                              verification steps, which could nullify your
                              insurance coverage.
                            </li>
                            <li>
                              A free registration will be created. No money will
                              be charged.
                            </li>
                            <li>
                              Your name will be recorded as the registrant for
                              this wrestler.
                            </li>
                          </ul>
                          <div class="flex align-items-center gap-x-xs">
                            <input
                              type="checkbox"
                              id="add_wrestler_agree"
                              v-model="agreedToTerms"
                            />
                            <label
                              for="add_wrestler_agree"
                              class="font-semi text-12 m-b-none cursor-pointer"
                              >I understand</label
                            >
                          </div>
                        </div>

                        <div class="m-t-sm">
                          <button
                            class="btn btn-primary btn-sm"
                            :disabled="
                              !agreedToTerms ||
                              addingWrestler ||
                              !paymentOptionId
                            "
                            @click="confirmAddWrestler"
                          >
                            <span v-if="addingWrestler">
                              <i class="fa-solid fa-spinner fa-spin"></i>
                              Registering...
                            </span>
                            <span v-else>
                              <i class="fa-solid fa-check"></i> Register Wrestler
                            </span>
                          </button>
                        </div>
                      </template>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Division & Team Overview -->
            <hr class="m-t-lg" />
            <div
              v-if="divisionsOverview.length > 0"
              class="grid grid-cols-3 sm-grid-cols-1 md-grid-cols-1 gap-md"
            >
              <div
                v-for="div in divisionsOverview"
                :key="'ov-' + div.id"
                class="gap-xs"
              >
                <h4 class="font-semi">
                  {{ div.name }}
                </h4>
                <div class="">
                  <button
                    v-for="team in div.teams"
                    :key="'nav-' + team.trdId"
                    class="btn btn-default btn-outline flex flex-col gap-xs"
                    @click="scrollToTeam(team.trdId)"
                  >
                    <span>
                      {{ team.name }}
                    </span>
                    <span :class="weightFillClass(team)">
                      {{ team.filledWeightClasses }}/{{
                        team.totalWeightClasses
                      }}
                      weights filled
                    </span>
                  </button>
                </div>
                <p v-if="div.teams.length === 0">No teams yet</p>
              </div>
            </div>

            <!-- Ineligible Wrestlers Warning -->
            <div
              v-if="ineligibleEntries.length > 0"
              class="m-t-md p-sm border border-solid rounded-sm border-yellow-200 bg-yellow-50"
            >
              <div class="flex align-items-center gap-xs m-b-xs">
                <i
                  class="fa-solid fa-triangle-exclamation"
                  style="color: #d97706"
                ></i>
                <strong class="text-14">
                  Unknown wrestler information
                </strong>
                <span class="label label-warning">
                  {{ ineligibleEntries.length }}
                </span>
              </div>
              <p class="m-b-xs">
                These wrestlers don't match any configured division and
                can't be auto-assigned to teams. Use the
                <i class="fa-solid fa-pencil"></i> icon to update their
                details (dob, gender, grade) and then add them to the
                right team.
              </p>
              <team-registration-pool
                :entries="ineligibleEntries"
                :paid-session-id="paidSessionId"
                :roster-names="rosterNameWithDivisionLookup"
                :all-divisions="divisions"
                @wrestler-assigned="handleWrestlerAssigned"
                @wrestler-unassigned="handleWrestlerUnassigned"
                @wrestler-division-changed="handleWrestlerDivisionChanged"
                @entry-removed="handleEntryRemoved"
                @wrestler-updated="handleWrestlerUpdated"
              ></team-registration-pool>
            </div>

            <!-- Settings -->
            <hr class="m-t-lg" />
            <div class="row m-none">
              <div class="col-md-4 p-x-n">
                <h1 class="form-section-title">Settings</h1>
              </div>
              <div class="col-md-8 p-x-n">
                <div class="flex align-items-center gap-xxs">
                  <label class="m-b-none">Per weight:</label>
                  <select
                    class="form-control input-sm"
                    style="width: auto"
                    :value="maxPerWeightClass"
                    @change="
                      updateMaxPerWeightClass(parseInt($event.target.value))
                    "
                  >
                    <option :value="1">1 wrestler</option>
                    <option :value="2">2 wrestlers</option>
                    <option :value="0">Unlimited</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Division Sections -->
    <div v-if="!loading">
      <div
        v-if="divisions.length === 0"
        class="text-gray-500 text-center p-y-md"
      >
        No divisions configured for this registration.
      </div>

      <div v-for="division in divisions" :key="division.id" class="ibox m-y-xs">
        <div class="ibox-content p-y-m">
          <div
            class="flex justify-space-between align-items-center cursor-pointer"
            @click="toggleDivision(division.id)"
          >
            <div class="flex align-items-center flex-wrap gap-sm">
              <i
                class="text-gray-400"
                style="font-size: 11px; width: 12px"
                :class="
                  isDivisionExpanded(division.id)
                    ? 'fa-solid fa-chevron-down'
                    : 'fa-solid fa-chevron-right'
                "
              ></i>
              <h2 class="m-y-none font-heavy">
                {{ divisionDisplayName(division) }}
              </h2>
              <p class="m-y-none font-normal">
                {{ getDivisionRosters(division).length }}
                {{ getDivisionRosters(division).length === 1 ? 'team' : 'teams' }} ·
                {{ getDivisionOpenSlots(division) }}
                {{ getDivisionOpenSlots(division) === 1 ? 'weight class' : 'weight classes' }} open ·
                {{ getDivisionPoolCount(division) }}
                {{ getDivisionPoolCount(division) === 1 ? 'wrestler' : 'wrestlers' }} ·
                {{ getDivisionUnassignedEntries(division).length }} unassigned
              </p>
            </div>
          </div>
          <div v-show="isDivisionExpanded(division.id)" class="m-t-md">
            <div class="grid grid-cols-2 md-grid-cols-1 gap-md">
              <!-- Pool for this division -->
              <div>
                <team-registration-pool
                  :entries="getDivisionPoolEntries(division)"
                  :paid-session-id="paidSessionId"
                  :roster-names="getDivisionRosterNames(division)"
                  :team-fill-data="getTeamFillData(division)"
                  :weight-classes="division.age_division.weight_classes || []"
                  :all-divisions="divisions"
                  @wrestler-assigned="handleWrestlerAssigned"
                  @wrestler-unassigned="handleWrestlerUnassigned"
                  @wrestler-division-changed="handleWrestlerDivisionChanged"
                  @entry-removed="handleEntryRemoved"
                  @wrestler-updated="handleWrestlerUpdated"
                ></team-registration-pool>
                <template v-if="getDivisionPoolEntries(division).length === 0">
                  <div
                    v-for="team in getTeamFillData(division)"
                    :key="'empty-fill-' + team.id"
                    class="p-x-xs p-y-xxs"
                  >
                    <div class="flex align-items-center gap-xs">
                      <span class="font-semi text-12">{{
                        getDivisionRosterNames(division)[team.id] || team.name
                      }}</span>
                      <span class="text-12 text-gray-500">
                        {{ team.filledCount }}/{{ team.totalWeightClasses }}
                        weights filled
                      </span>
                    </div>
                  </div>
                  <div
                    class="font-italic text-gray-400 text-center p-y-md border border-dashed border-gray-300 rounded-sm m-b-sm text-14"
                  >
                    Wrestlers who have applied will appear here.
                  </div>
                </template>
              </div>

              <!-- Teams -->
              <div class="flex flex-col gap-m">
                <team-registration-team-card
                  v-for="rosterData in getDivisionRosters(division)"
                  :key="rosterData.team_registration_division_id"
                  :ref="'team-card-' + rosterData.team_registration_division_id"
                  :roster="rosterData"
                  :weight-classes="division.age_division.weight_classes || []"
                  :age-division="division.age_division"
                  :team-registration-division-id="
                    rosterData.team_registration_division_id
                  "
                  :paid-session-id="paidSessionId"
                  :paid-session-name="paidSessionName"
                  :age-division-name="divisionDisplayName(division)"
                  :team-entries="getTeamPoolEntries(rosterData.id)"
                  :expanded="
                    isTeamExpanded(rosterData.team_registration_division_id)
                  "
                  :unassigned-pool-entries="
                    getDivisionUnassignedEntries(division)
                  "
                  :max-per-weight-class="maxPerWeightClass"
                  :other-team-entries="
                    getOtherTeamEntries(division, rosterData.id)
                  "
                  @deleted="handleTeamDeleted"
                  @name-updated="handleTeamNameUpdated(rosterData, $event)"
                  @approve-plan="approvePlan"
                  @deny-plan="denyPlan"
                  @finalize-team="finalizeTeam"
                  @quick-assign="handleQuickAssign"
                  @quick-swap="handleQuickSwap"
                  @toggle-expanded="
                    toggleTeamExpanded(rosterData.team_registration_division_id)
                  "
                  @pool-entry-updated="loadPoolEntries"
                ></team-registration-team-card>
                <button
                  class="w-full-xs-only align-self-flex-end btn btn-primary btn-outline"
                  @click="addTeam(division)"
                >
                  <i class="fa-solid fa-plus m-r-xxs"></i>
                  Add Team
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import swal from 'sweetalert2';
import ReportForm from './report_form.vue';
import TeamRegistrationPool from './team_registration_pool.vue';
import TeamRegistrationTeamCard from './team_registration_team_card.vue';
import Spinner from './spinner.vue';

export default {
  name: 'team-registration-manager',
  components: {
    ReportForm,
    TeamRegistrationPool,
    TeamRegistrationTeamCard,
    Spinner,
  },
  props: {
    paidSessionId: {
      type: Number,
      required: true,
    },
    eventName: {
      type: String,
      default: '',
    },
    paidSession: {
      type: Object,
      default() {
        return {};
      },
    },
    teamSlug: {
      type: String,
      default: '',
    },
    coupon: {
      type: Object,
      default() {
        return {};
      },
    },
  },
  data() {
    return {
      loadingRosters: true,
      loadingPool: true,
      divisions: [],
      poolEntries: [],
      showAddWrestler: false,
      addWrestlerSearch: '',
      addWrestlerResults: [],
      addWrestlerLoading: false,
      addWrestlerTimeout: null,
      selectedWrestlerForAdd: null,
      paymentOptionId: null,
      agreedToTerms: false,
      addingWrestler: false,
      creatingInitialTeams: false,
      maxPerWeightClass: 1,
      paidSessionName: '',
      collapsedDivisions: {},
      expandedTeams: {},
    };
  },
  computed: {
    loading() {
      return this.loadingRosters || this.loadingPool;
    },
    registrationUrl() {
      if (!this.paidSession || !this.paidSession.registration_url) return null;
      if (!this.teamSlug || !this.paidSession.slug) {
        return this.paidSession.registration_url;
      }
      return `${window.location.origin}/${this.teamSlug}/register/${encodeURIComponent(this.paidSession.slug)}`;
    },
    rosterNameLookup() {
      const lookup = {};
      this.divisions.forEach((division) => {
        (division.rosters || []).forEach((roster) => {
          lookup[roster.id] = roster.name;
        });
      });
      return lookup;
    },
    rosterNameWithDivisionLookup() {
      const lookup = {};
      const prefix = this.paidSessionName;
      this.divisions.forEach((division) => {
        const divLabel = this.divisionDisplayName(division);
        const divName = division.age_division.name;
        (division.rosters || []).forEach((roster) => {
          const namePrefix = prefix ? `${prefix} - ${divName} ` : '';
          const shortName = namePrefix && roster.name.startsWith(namePrefix)
            ? roster.name.slice(namePrefix.length)
            : roster.name;
          lookup[roster.id] = `${divLabel} - ${shortName}`;
        });
      });
      return lookup;
    },
    poolEntriesByDivision() {
      const map = {};
      this.poolEntries.forEach((entry) => {
        const divId = entry.age_division ? entry.age_division.id : 'none';
        if (!map[divId]) map[divId] = [];
        map[divId].push(entry);
      });
      return map;
    },
    configuredDivisionIds() {
      return new Set(this.divisions.map((d) => d.age_division.id));
    },
    ineligibleEntries() {
      return this.poolEntries.filter((entry) => {
        if (!entry.age_division) return true;
        return !this.configuredDivisionIds.has(entry.age_division.id);
      });
    },
    unassignedPoolCount() {
      return this.poolEntries.filter((e) => !e.assigned_roster).length;
    },
    totalTeamCount() {
      let count = 0;
      this.divisions.forEach((d) => {
        count += (d.rosters || []).length;
      });
      return count;
    },
    divisionsOverview() {
      return this.divisions.map((division) => {
        const rosters = division.rosters || [];
        const wcs = division.age_division.weight_classes || [];
        const rosterNames = this.getDivisionRosterNames(division);
        const teams = rosters.map((roster) => {
          const entries = this.getTeamPoolEntries(roster.id);
          const filledWcs = {};
          entries.forEach((e) => {
            if (e.weight_class) {
              filledWcs[e.weight_class.id] = true;
            }
          });
          return {
            id: roster.id,
            trdId: roster.team_registration_division_id,
            name: rosterNames[roster.id] || roster.name,
            memberCount: entries.length,
            filledWeightClasses: Object.keys(filledWcs).length,
            totalWeightClasses: wcs.length,
          };
        });
        return {
          id: division.id,
          name: this.divisionDisplayName(division),
          teams,
          poolCount: this.getDivisionPoolCount(division),
        };
      });
    },
  },
  created() {
    this.loadRosters(true);
    this.loadPoolEntries(true);
  },
  mounted() {
    if (
      this.paidSession.payment_options
      && this.paidSession.payment_options.length === 1
    ) {
      this.paymentOptionId = this.paidSession.payment_options[0].id;
    }
  },
  methods: {
    async copyRegistrationUrl() {
      const url = this.registrationUrl;
      if (!url) return;
      try {
        await navigator.clipboard.writeText(url);
        this.$notificationManager.emit(
          'show-toast',
          'Registration link copied to clipboard',
          true,
        );
      } catch (e) {
        this.$notificationManager.emit(
          'show-toast',
          'Unable to copy link to clipboard',
          false,
        );
      }
    },
    weightFillClass(team) {
      if (team.totalWeightClasses === 0) return '';
      const ratio = team.filledWeightClasses / team.totalWeightClasses;
      if (ratio >= 1) return 'text-green-600';
      if (ratio >= 0.5) return 'text-yellow-600';
      return 'text-red-600';
    },
    loadRosters(showSpinner) {
      const vm = this;
      if (showSpinner) vm.loadingRosters = true;
      axios
        .get(
          `/api/v1/paid_sessions/${vm.paidSessionId}/team_registration_rosters`,
        )
        .then((response) => {
          vm.divisions = response.data.divisions || [];
          const apiMax = response.data.max_wrestlers_per_weight_class;
          vm.maxPerWeightClass = apiMax === null || apiMax === undefined ? 1 : apiMax;
          vm.paidSessionName = response.data.paid_session_name || '';
          vm.loadingRosters = false;
          vm.ensureFirstTeams();
        })
        .catch(() => {
          vm.loadingRosters = false;
          vm.$notificationManager.emit(
            'show-toast',
            'Error loading teams',
            false,
          );
        });
    },
    loadPoolEntries(showSpinner) {
      const vm = this;
      if (showSpinner) vm.loadingPool = true;
      const url = `/api/v1/paid_sessions/${vm.paidSessionId}/team_registration_pool_entries`;
      // This endpoint paginates (30/page), so follow the Link headers to load every wrestler.
      vm.$apiService
        .loadAllPages(url, 'pool_entries')
        .then((entries) => {
          vm.poolEntries = entries || [];
          vm.loadingPool = false;
        })
        .catch(() => {
          vm.loadingPool = false;
          vm.$notificationManager.emit(
            'show-toast',
            'Error loading pool entries',
            false,
          );
        });
    },
    getTeamPoolEntries(rosterId) {
      return this.poolEntries.filter((e) => e.roster_id === rosterId);
    },
    scrollToTeam(trdId) {
      const vm = this;
      // Expand the division containing this team
      vm.divisions.forEach((division) => {
        const hasTeam = (division.rosters || []).some(
          (r) => r.team_registration_division_id === trdId,
        );
        if (hasTeam && !vm.isDivisionExpanded(division.id)) {
          vm.$set(vm.collapsedDivisions, division.id, false);
        }
      });
      vm.$nextTick(() => {
        const refKey = `team-card-${trdId}`;
        const ref = vm.$refs[refKey];
        const el = Array.isArray(ref) ? ref[0].$el : ref ? ref.$el : null;
        if (el) {
          el.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }
      });
    },
    divisionDisplayName(division) {
      const { name } = division.age_division;
      const { gender } = division.age_division;
      if (gender === 'male') return `${name} (Boys)`;
      if (gender === 'female') return `${name} (Girls)`;
      return name;
    },
    getDivisionPoolEntries(division) {
      return this.poolEntriesByDivision[division.age_division.id] || [];
    },
    getDivisionPoolCount(division) {
      return this.getDivisionPoolEntries(division).length;
    },
    getDivisionRosters(division) {
      return division.rosters || [];
    },
    getTeamFillData(division) {
      const weightClasses = division.age_division.weight_classes || [];
      const rosters = division.rosters || [];
      return rosters.map((roster) => {
        const entries = this.getTeamPoolEntries(roster.id);
        const filledWcIds = {};
        entries.forEach((e) => {
          if (e.weight_class) {
            filledWcIds[e.weight_class.id] = true;
          }
        });
        const missing = weightClasses.filter((wc) => !filledWcIds[wc.id]);
        return {
          id: roster.id,
          name: roster.name,
          totalWeightClasses: weightClasses.length,
          filledCount: Object.keys(filledWcIds).length,
          missingWeightClasses: missing.map((wc) => wc.display_name),
        };
      });
    },
    getDivisionRosterNames(division) {
      const lookup = {};
      const prefix = this.paidSessionName;
      const divName = this.divisionDisplayName(division);
      const namePrefix = prefix ? `${prefix} - ${divName} ` : '';
      (division.rosters || []).forEach((roster) => {
        lookup[roster.id] = namePrefix && roster.name.startsWith(namePrefix)
          ? roster.name.slice(namePrefix.length)
          : roster.name;
      });
      return lookup;
    },
    getDivisionOpenSlots(division) {
      return this.getTeamFillData(division).reduce(
        (sum, t) => sum + (t.totalWeightClasses - t.filledCount),
        0,
      );
    },
    getDivisionUnassignedEntries(division) {
      return this.getDivisionPoolEntries(division).filter(
        (e) => !e.assigned_roster,
      );
    },
    isTeamExpanded(trdId) {
      return this.expandedTeams[trdId] === true;
    },
    toggleTeamExpanded(trdId) {
      this.$set(this.expandedTeams, trdId, !this.expandedTeams[trdId]);
    },
    isDivisionExpanded(divisionId) {
      return this.collapsedDivisions[divisionId] !== true;
    },
    toggleDivision(divisionId) {
      this.$set(
        this.collapsedDivisions,
        divisionId,
        !this.collapsedDivisions[divisionId],
      );
    },
    // Add wrestler methods
    onAddWrestlerSearch() {
      const vm = this;
      clearTimeout(vm.addWrestlerTimeout);

      if (vm.addWrestlerSearch.length < 2) {
        vm.addWrestlerResults = [];
        return;
      }

      vm.addWrestlerTimeout = setTimeout(() => {
        vm.addWrestlerLoading = true;
        axios
          .get(
            `/api/v1/wrestlers?query=${encodeURIComponent(
              vm.addWrestlerSearch,
            )}&per_page=20`,
          )
          .then((response) => {
            vm.addWrestlerResults = response.data.wrestlers || [];
            vm.addWrestlerLoading = false;
          })
          .catch(() => {
            vm.addWrestlerLoading = false;
          });
      }, 300);
    },
    isAlreadyInPool(wrestlerId) {
      return this.poolEntries.some((e) => e.wrestler_profile.id === wrestlerId);
    },
    selectWrestlerForAdd(wrestler) {
      if (this.isAlreadyInPool(wrestler.id)) return;
      this.selectedWrestlerForAdd = wrestler;
      this.agreedToTerms = false;
    },
    closeAddWrestler() {
      this.showAddWrestler = false;
      this.selectedWrestlerForAdd = null;
      this.addWrestlerSearch = '';
      this.addWrestlerResults = [];
      this.agreedToTerms = false;
      this.addingWrestler = false;
    },
    confirmAddWrestler: _.throttle(function () {
      if (!this.agreedToTerms || this.addingWrestler || !this.paymentOptionId) return;

      const vm = this;
      vm.addingWrestler = true;

      const url = vm.$apiService.checkoutsUrl();
      const payload = {
        payment_method_type: 'cash',
        payment_method_id: null,
        auto_approve: true,
        payment_plan: {
          registrations: [
            {
              payment_option_id: vm.paymentOptionId,
              registerable: {
                id: vm.selectedWrestlerForAdd.id,
                type: 'WrestlerProfile',
              },
            },
          ],
          coupon: vm.coupon.code,
          donation_amount: 0,
          insurance_quotes: [],
          billing_plans_added: [],
        },
      };

      axios
        .post(url, payload)
        .then(() => {
          vm.$notificationManager.emit(
            'show-toast',
            `${vm.selectedWrestlerForAdd.full_name} registered successfully`,
            true,
          );
          vm.closeAddWrestler();
          vm.loadPoolEntries();
        })
        .catch((error) => {
          const msg = error.response && error.response.data && error.response.data.errors
            ? error.response.data.errors.join(', ')
            : 'Failed to register wrestler';
          vm.$notificationManager.emit('show-toast', msg, false);
          vm.addingWrestler = false;
        });
    }, 500),
    // Approve/Deny payment plans
    approvePlan(plan) {
      const vm = this;
      vm.$set(plan, 'approving', true);
      axios
        .post(
          `/api/v1/paid_sessions/${vm.paidSessionId}/payment_plans/${plan.id}/approve`,
        )
        .then((response) => {
          const msg = response.data.status === 'approved_payment_failed'
            ? `Approved but payment failed: ${response.data.error}`
            : 'Payment plan approved';
          vm.$notificationManager.emit(
            'show-toast',
            msg,
            response.data.status !== 'approved_payment_failed',
          );
          vm.loadPoolEntries();
        })
        .catch((error) => {
          const msg = error.response && error.response.data && error.response.data.errors
            ? error.response.data.errors.join(', ')
            : 'Failed to approve';
          vm.$notificationManager.emit('show-toast', msg, false);
        })
        .finally(() => {
          vm.$set(plan, 'approving', false);
        });
    },
    denyPlan(plan) {
      const vm = this;
      swal.fire({
        icon: 'warning',
        title: 'Deny this registration?',
        text: 'This will cancel their payment plan and remove them from the team.',
        showCancelButton: true,
        reverseButtons: true,
        confirmButtonText: 'Yes, deny',
        buttonsStyling: false,
        customClass: {
          confirmButton: 'btn btn-danger m-l-md',
          cancelButton: 'btn btn-default',
        },
      }).then((result) => {
        if (!result.value) return;
        vm.$set(plan, 'denying', true);
        axios
          .post(
            `/api/v1/paid_sessions/${vm.paidSessionId}/payment_plans/${plan.id}/deny`,
          )
          .then(() => {
            vm.$notificationManager.emit(
              'show-toast',
              'Payment plan denied',
              true,
            );
            vm.loadPoolEntries();
          })
          .catch((error) => {
            const msg = error.response && error.response.data && error.response.data.errors
              ? error.response.data.errors.join(', ')
              : 'Failed to deny';
            vm.$notificationManager.emit('show-toast', msg, false);
          })
          .finally(() => {
            vm.$set(plan, 'denying', false);
          });
      });
    },
    // Finalize team: batch approve all awaiting_approval payment plans
    finalizeTeam(plans) {
      const vm = this;
      const promises = plans.map((plan) => axios
        .post(
          `/api/v1/paid_sessions/${vm.paidSessionId}/payment_plans/${plan.id}/approve`,
        )
        .catch((error) => ({ error, planId: plan.id })));

      Promise.all(promises).then((results) => {
        const failures = results.filter((r) => r && r.error);
        if (failures.length === 0) {
          vm.$notificationManager.emit(
            'show-toast',
            `${plans.length} payment plan(s) approved`,
            true,
          );
        } else {
          vm.$notificationManager.emit(
            'show-toast',
            `${plans.length - failures.length} approved, ${
              failures.length
            } failed`,
            false,
          );
        }
        vm.loadPoolEntries();
      });
    },
    // Event handlers from pool components
    handleEntryRemoved(entryId) {
      this.poolEntries = this.poolEntries.filter((e) => e.id !== entryId);
    },
    handleWrestlerAssigned() {
      this.loadPoolEntries();
    },
    handleWrestlerUnassigned() {
      this.loadPoolEntries();
    },
    handleWrestlerDivisionChanged() {
      this.loadPoolEntries();
    },
    // Team methods
    addTeam(division) {
      const vm = this;
      const teamCount = (division.rosters || []).length + 1;
      const name = `Team ${teamCount}`;

      axios
        .post(
          `/api/v1/paid_sessions/${vm.paidSessionId}/team_registration_rosters`,
          {
            age_division_id: division.age_division.id,
            name,
          },
        )
        .then(() => {
          vm.loadRosters();
          vm.$notificationManager.emit('show-toast', `${name} created`, true);
        })
        .catch((error) => {
          const msg = error.response?.data?.errors
            ? error.response.data.errors.join(', ')
            : 'Failed to create team';
          vm.$notificationManager.emit('show-toast', msg, false);
        });
    },
    ensureFirstTeams() {
      if (this.creatingInitialTeams) return;

      const divisionsNeedingTeams = this.divisions.filter(
        (d) => (d.rosters || []).length === 0,
      );
      if (divisionsNeedingTeams.length === 0) return;

      this.creatingInitialTeams = true;
      const vm = this;

      const promises = divisionsNeedingTeams.map((division) => axios.post(
        `/api/v1/paid_sessions/${vm.paidSessionId}/team_registration_rosters`,
        { age_division_id: division.age_division.id, name: 'Team 1' },
      ));

      Promise.all(promises)
        .then(() => {
          vm.loadRosters();
        })
        .catch(() => {
          vm.$notificationManager.emit(
            'show-toast',
            'Failed to create initial teams',
            false,
          );
        })
        .finally(() => {
          vm.creatingInitialTeams = false;
        });
    },
    handleTeamDeleted() {
      this.loadRosters();
      this.loadPoolEntries();
    },
    handleWrestlerUpdated() {
      // Reload pool entries so server recalculates eligibility
      this.loadPoolEntries();
    },
    handleTeamNameUpdated(rosterData, newName) {
      rosterData.name = newName;
    },
    getOtherTeamEntries(division, currentRosterId) {
      const grouped = {};
      const assignedEntries = this.getDivisionPoolEntries(division).filter(
        (e) => e.assigned_roster && e.assigned_roster.id !== currentRosterId,
      );
      assignedEntries.forEach((entry) => {
        const wcId = entry.weight_class ? entry.weight_class.id : null;
        if (wcId) {
          if (!grouped[wcId]) grouped[wcId] = [];
          grouped[wcId].push(entry);
        }
      });
      return grouped;
    },
    handleQuickAssign({ entry, weightClassId, rosterId }) {
      const vm = this;
      const rosterName = vm.rosterNameLookup[rosterId] || 'Team';

      vm.$set(entry, 'assigning', true);

      axios
        .patch(
          `/api/v1/paid_sessions/${vm.paidSessionId}/team_registration_pool_entries/${entry.id}/assign`,
          { roster_id: rosterId, weight_class_id: weightClassId },
        )
        .then(() => {
          vm.$notificationManager.emit(
            'show-toast',
            `${entry.wrestler_profile.full_name} added to ${rosterName}`,
            true,
          );
          vm.loadPoolEntries();
        })
        .catch((error) => {
          vm.$set(entry, 'assigning', false);
          const msg = error.response?.data?.errors
            ? error.response.data.errors.join(', ')
            : 'Failed to assign wrestler';
          vm.$notificationManager.emit('show-toast', msg, false);
        });
    },
    handleQuickSwap({ entry, weightClassId, rosterId }) {
      const vm = this;
      const rosterName = vm.rosterNameLookup[rosterId] || 'Team';

      vm.$set(entry, 'assigning', true);
      axios
        .patch(
          `/api/v1/paid_sessions/${vm.paidSessionId}/team_registration_pool_entries/${entry.id}/assign`,
          { roster_id: rosterId, weight_class_id: weightClassId },
        )
        .then(() => {
          vm.$notificationManager.emit(
            'show-toast',
            `Swapped: ${entry.wrestler_profile.full_name} → ${rosterName}`,
            true,
          );
          vm.loadPoolEntries();
        })
        .catch((error) => {
          vm.$set(entry, 'assigning', false);
          const msg = error.response?.data?.errors
            ? error.response.data.errors.join(', ')
            : 'Failed to swap wrestler';
          vm.$notificationManager.emit('show-toast', msg, false);
        });
    },
    updateMaxPerWeightClass(value) {
      const vm = this;
      vm.maxPerWeightClass = value;

      axios
        .patch(
          `/api/v1/paid_sessions/${vm.paidSessionId}/team_registration_rosters/update_settings`,
          { max_wrestlers_per_weight_class: value || null },
        )
        .then(() => {
          vm.$notificationManager.emit(
            'show-toast',
            value === 0
              ? 'Set to unlimited wrestlers per weight'
              : `Set to ${value} wrestler(s) per weight`,
            true,
          );
        })
        .catch(() => {
          vm.$notificationManager.emit(
            'show-toast',
            'Failed to update setting',
            false,
          );
        });
    },
  },
};
</script>
